import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dumbbell,
  TrendingUp,
  Activity,
  Target,
  Calendar,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const LIFT_COLORS = {
  SQUAT: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
  TRAPBAR: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  BENCH: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  ROW: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
  HIP_THRUST: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  CUSTOM: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400',
};

const AthleteVBTPage = () => {
  const { api, user } = useAuth();
  const { t, language } = useLanguage();

  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user?.athlete_id) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.get(`/vbt/athlete/${user.athlete_id}/profile`);
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch VBT profile:', err);
      // Don't show error toast for athletes - they might just not have VBT data
    } finally {
      setIsLoading(false);
    }
  }, [api, user?.athlete_id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  if (!profile || !profile.stats?.total_tests) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 p-4 lg:p-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Dumbbell className="w-6 h-6 text-amber-500" />
            VBT
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Velocity-Based Training</p>
        </div>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-8 text-center">
            <Dumbbell className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-100 mb-2">
              {language === 'IT' ? 'Nessun Test VBT' : 'No VBT Tests'}
            </h3>
            <p className="text-zinc-500">
              {language === 'IT' 
                ? 'Non hai ancora test VBT registrati. Contatta il tuo preparatore atletico.'
                : 'You don\'t have any VBT tests recorded yet. Contact your strength coach.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { athlete, latest_tests, all_tests, active_prescriptions, recent_executions, stats } = profile;

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4 lg:p-6 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
          <Dumbbell className="w-6 h-6 text-amber-500" />
          {language === 'IT' ? 'Il Tuo VBT' : 'Your VBT'}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Velocity-Based Training</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Activity className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{stats.total_tests}</div>
            <div className="text-xs text-zinc-500 uppercase">
              {language === 'IT' ? 'Test' : 'Tests'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Target className="w-5 h-5 text-amber-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{active_prescriptions.length}</div>
            <div className="text-xs text-zinc-500 uppercase">
              {language === 'IT' ? 'Prescrizioni' : 'Prescriptions'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Dumbbell className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{stats.lift_types_tested.length}</div>
            <div className="text-xs text-zinc-500 uppercase">
              {language === 'IT' ? 'Esercizi' : 'Lifts'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Profiles */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100">
            {language === 'IT' ? 'I Tuoi Profili Load-Velocity' : 'Your Load-Velocity Profiles'}
          </CardTitle>
          <CardDescription className="text-zinc-500">
            {language === 'IT' ? 'Stime 1RM basate sui test recenti' : '1RM estimates based on recent tests'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(latest_tests).map(([liftType, test]) => (
              <div
                key={liftType}
                className={`p-4 rounded-sm border ${LIFT_COLORS[liftType] || LIFT_COLORS.CUSTOM}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold">{liftType}</span>
                  <span className="text-xs text-zinc-500">{test.date}</span>
                </div>
                {test.lv_profile ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500">1RM Stimato</span>
                      <span className="text-2xl font-bold text-zinc-100">{test.lv_profile.l0} kg</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500">V0</span>
                      <span className="text-sm text-zinc-300">{test.lv_profile.v0} m/s</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-zinc-500">
                    {language === 'IT' ? 'Profilo non disponibile' : 'Profile not available'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Active Prescriptions */}
      {active_prescriptions.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {language === 'IT' ? 'Le Tue Prescrizioni' : 'Your Prescriptions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {active_prescriptions.map((rx) => (
                <div
                  key={rx.id}
                  className="p-4 bg-amber-500/5 border border-amber-500/30 rounded-sm"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-zinc-100">
                        {rx.lift_type} - {rx.focus}
                      </div>
                      <div className="text-sm text-zinc-400 mt-1">
                        {rx.sets} set × {rx.reps} rep @ {rx.load_range_kg?.[0]}-{rx.load_range_kg?.[1]} kg
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {language === 'IT' ? 'Velocità target:' : 'Target velocity:'} {rx.velocity_target?.[0]}-{rx.velocity_target?.[1]} m/s
                      </div>
                      {rx.velocity_stop && (
                        <div className="text-xs text-amber-400 mt-1">
                          {language === 'IT' ? 'Ferma se velocità scende sotto' : 'Stop if velocity drops below'} {rx.velocity_stop} m/s
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-semibold">
                        {rx.status}
                      </span>
                      <div className="text-xs text-zinc-500 mt-2">{rx.date}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Executions */}
      {recent_executions.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {language === 'IT' ? 'Esecuzioni Recenti' : 'Recent Executions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recent_executions.slice(0, 5).map((exe) => (
                <div
                  key={exe.id}
                  className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800"
                >
                  <div>
                    <div className="font-medium text-zinc-100">{exe.lift_type}</div>
                    <div className="text-sm text-zinc-400">
                      {exe.total_sets}×{exe.total_reps} @ {exe.avg_load_kg}kg • {exe.avg_v_mean} m/s
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500">{exe.date}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test History */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100">
            {language === 'IT' ? 'Storico Test' : 'Test History'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {all_tests.slice(0, 10).map((test) => (
              <div
                key={test.id}
                className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    LIFT_COLORS[test.lift_type]?.includes('blue') ? 'bg-blue-400' :
                    LIFT_COLORS[test.lift_type]?.includes('emerald') ? 'bg-emerald-400' :
                    LIFT_COLORS[test.lift_type]?.includes('amber') ? 'bg-amber-400' :
                    LIFT_COLORS[test.lift_type]?.includes('purple') ? 'bg-purple-400' :
                    LIFT_COLORS[test.lift_type]?.includes('rose') ? 'bg-rose-400' :
                    'bg-zinc-400'
                  }`} />
                  <div>
                    <span className="font-medium text-zinc-100">{test.lift_type}</span>
                    {test.custom_lift_name && (
                      <span className="text-zinc-500"> - {test.custom_lift_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {test.lv_profile && (
                    <span className="text-sm text-zinc-400">
                      1RM: <span className="text-zinc-100 font-medium">{test.lv_profile.l0}kg</span>
                    </span>
                  )}
                  <span className="text-xs text-zinc-500">{test.date}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AthleteVBTPage;
